import React, {type FC, useEffect, useRef, useState} from 'react';
import {BackgroundVideo, ConnectionErrorOverlay, StreamGrid} from '../types/Streaming.styled.ts';
import useGlobalStore from '../../../store/useGlobalStore.ts';
import useStreamingStore from '../../../store/useStreamingStore.ts';
import useJitsiConnection from '../../../hooks/useJitsiConnection.tsx';
import LocalCell from './LocalCell.tsx';
import StudioCell from './StudioCell.tsx';
import {getCellLayout} from '../../../utils/StreamingUtil.ts';
import RemoteCell from './RemoteCell.tsx';

const CONNECTION_RETRY_INTERVAL_MS = 15000;

const Streaming: FC = () => {
    const isWatchingPage = location.pathname.includes('Watching');
    const {isFullScreen} = useGlobalStore();
    const {state, streamingPollingData, gridLayout, connectionError} = useStreamingStore();
    const [retryNonce, setRetryNonce] = useState(0);
    const {
        getConnection,
        leave,
        studioTrack,
        localTrack,
        localTrackVersion,
        remoteTrackVersion,
        remoteTracks,
        displayedParticipants,
        triggerConstraintUpdate,
    } = useJitsiConnection();

    const gridInfo = getCellLayout(gridLayout.mode);

    // connectionError 발생 중에는 15초 간격으로 retryNonce를 증가시켜 초기화 effect를 재실행(재연결)한다.
    // connectionError가 false로 전이되면(재연결 성공) 즉시 정지.
    useEffect(() => {
        if (!connectionError) return;
        const interval = setInterval(() => {
            setRetryNonce((v) => v + 1);
        }, CONNECTION_RETRY_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [connectionError]);

    useEffect(() => {
        if (!state.jwt) return;

        const initializeJitsi = async () => {
            try {
                if (window.JitsiMeetJS) {
                    window.JitsiMeetJS.init({
                        disableAudioLevels: true,
                        disableSimulcast: false,
                        enableWindowOnErrorHandler: false,
                        disableThirdPartyRequests: true,
                        useIPv6: false,
                        enableAnalyticsLogging: false,
                    });
                    await getConnection();
                }
            } catch (error) {
                console.error('Jitsi load Fail', error);
            }
        };

        initializeJitsi();
        // retryNonce: connectionError 재연결 재시도 트리거(같은 jwt라도 재실행되도록)
    }, [state.jwt, retryNonce]);

    useEffect(() => {
        return () => {
            leave();
        };
    }, [leave]);

    const studioElemRef = useRef<HTMLVideoElement>(null);
    const localElemRef = useRef<HTMLVideoElement>(null);
    const remotesElemRef = useRef<Array<HTMLVideoElement>>([]);

    return (
        <BackgroundVideo $isFullScreen={isFullScreen} $isBroadCasting={streamingPollingData?.broadcast?.content_type}>
            <StreamGrid col={gridInfo.gridCol} row={gridInfo.gridRow}>
                {/* Studio Cell */}
                <StudioCell studioElemRef={studioElemRef} studioTrack={studioTrack} gridInfo={gridInfo}/>
                {/* Local Cell */}
                <LocalCell localElemRef={localElemRef} localTrack={localTrack} localTrackVersion={localTrackVersion} gridInfo={gridInfo}/>
                {/* Remote Cell */}
                <RemoteCell
                    remoteElemRef={remotesElemRef}
                    remoteTracks={remoteTracks}
                    remoteTrackVersion={remoteTrackVersion}
                    remoteSize={gridInfo.remoteSize}
                    isWatching={isWatchingPage}
                    displayedParticipants={displayedParticipants}
                    onConstraintUpdate={triggerConstraintUpdate}
                />
            </StreamGrid>
            {connectionError && (
                <ConnectionErrorOverlay>
                    <span>방송 연결이 끊어졌습니다. 다시 연결하는 중...</span>
                </ConnectionErrorOverlay>
            )}
        </BackgroundVideo>
    );
};

export default Streaming;
